// src/features/user-portfolios/pages/PortfolioDashboardPage.tsx
//
// Phase 5 — the master analytics dashboard for a single portfolio. Fetches the
// FIFO-analysed payload (Numba + Polars on the backend), then renders:
//   - a KPI strip (unrealized / realized / YTD vs S&P 500 / inception),
//   - a Recharts allocation donut (by market value),
//   - best/worst performer widgets, and
//   - the sortable TanStack ledger grid.
// Wrapped in the shared Layout/Sidebar shell and theme-aware (light + dark) to
// match the rest of the app.

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { GlobalSearchBar } from '../../global-search';
import { Layout, Sidebar } from '../../../shared/ui';
import {
    exportPortfolioCsv,
    getPortfolioDashboard,
    updatePortfolio,
    type CurrencyCode,
    type DashboardResponse,
    type PositionRow,
} from '../api';
import { PortfolioPerformanceChart } from '../components/PortfolioPerformanceChart';
import { computeRebasedPerformance, type Period } from '../utils/performance';
import { PortfolioPositionsTable } from '../components/PortfolioPositionsTable';
import { TradeHistoryTable } from '../components/TradeHistoryTable';
import { buildInstrumentRoute } from '../utils/instrumentRoute';

// Selectable benchmarks for the performance chart (sent as the `benchmark`
// query param to the dashboard endpoint, which pulls its FMP history live).
const AVAILABLE_BENCHMARKS = [
    { label: 'S&P 500', symbol: 'SPY' },
    { label: 'Nasdaq 100', symbol: 'QQQ' },
    { label: 'Dow Jones', symbol: 'DIA' },
    { label: 'Russell 2000', symbol: 'IWM' },
    { label: 'Russell 1000', symbol: 'IWB' },
    { label: 'MSCI World ex-US (EFA)', symbol: 'EFA' },
    { label: 'Total World Stock (VT)', symbol: 'VT' },
    { label: 'DAX (German Index)', symbol: '^GDAXI' },
    { label: 'ATX (Austrian Index)', symbol: '^ATX' },
];

// Expanded categorical palette so highly-diversified sector / asset-class
// breakdowns never run out of distinct colours (wraps with modulo).
const COLORS = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#f43f5e',
    '#84cc16',
    '#14b8a6',
    '#f97316',
    '#6366f1',
    '#ec4899',
];

// Native, globally-correct money formatters via Intl.NumberFormat so ANY base
// currency renders its proper symbol ($, €, £, CA$, MX$, ¥...). Falls back to
// an ISO-prefixed number for exotic/unknown codes instead of throwing.
const money = (val: number, ccy: string = 'USD') => {
    const code = (ccy || 'USD').toUpperCase();

    // FIX: Explicitly intercept GBX so the browser doesn't auto-render "GBp".
    if (code === 'GBX') {
        return `GBX ${Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
        }).format(val || 0);
    } catch {
        return `${code} ${Number(val || 0).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }
};

const signedMoney = (val: number, ccy: string = 'USD') => {
    const code = (ccy || 'USD').toUpperCase();
    const v = Number(val) || 0;
    const sign = v > 0 ? '+' : v < 0 ? '-' : '';

    if (code === 'GBX') {
        return `${sign}${money(Math.abs(v), code)}`;
    }

    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: code,
            signDisplay: 'exceptZero',
        }).format(v);
    } catch {
        return `${sign}${money(Math.abs(v), code)}`;
    }
};

// Return-based KPI colouring: GREEN for a positive return, RED for a
// negative one, and ORANGE for a flat 0.00%. Shared by every performance
// KPI card + the performer widgets so the whole dashboard reads consistently.
const moneyColor = (val: number) =>
    val > 0
        ? 'text-emerald-600 dark:text-emerald-400'
        : val < 0
          ? 'text-red-600 dark:text-red-400'
          : 'text-orange-500 dark:text-orange-400';

// --- Best/worst performer widget ---------------------------------------
const PerformersList = ({
    title,
    list,
    metricKey,
}: {
    title: string;
    list: PositionRow[] | undefined;
    metricKey: 'unrealized_pnl_percent' | 'daily_pnl_percent';
}) => (
    <div className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
        <h3 className="mb-4 border-b border-slate-100 dark:border-slate-800 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            {title}
        </h3>
        <div className="space-y-3">
            {list?.slice(0, 4).map((p) => {
                const val = p[metricKey] || 0;
                return (
                    <div
                        key={p.symbol}
                        className="flex items-center justify-between text-sm"
                    >
                        <Link
                            to={buildInstrumentRoute(p.symbol, p.instrument_type)}
                            className="font-bold text-slate-900 transition hover:text-blue-600 hover:underline dark:text-white dark:hover:text-blue-400"
                        >
                            {p.symbol}
                        </Link>
                        <span className={`font-mono ${moneyColor(val)}`}>
                            {val > 0 ? '+' : ''}
                            {val.toFixed(2)}%
                        </span>
                    </div>
                );
            })}
            {(!list || list.length === 0) && (
                <span className="text-xs text-slate-400 dark:text-slate-500">
                    No data available.
                </span>
            )}
        </div>
    </div>
);

// --- Reusable allocation donut -----------------------------------------
// One donut chart with an exact "$value (pct%)" tooltip. Reused for the
// market-value, sector and asset-class breakdowns so all three stay visually
// consistent. `total` is the sum used to derive each slice's percentage.
const AllocationDonut = ({
    title,
    data,
    total,
    currency = 'USD',
}: {
    title: string;
    data: { name: string; value: number }[];
    total: number;
    currency?: string;
}) => {
    // Local Intl formatter so slices render the portfolio's base-currency symbol
    // (tooltip shows cents; the compact legend rounds to whole units).
    const fmtMoney = (v: number, maxDecimals = 2) => {
        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency || 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: maxDecimals,
            }).format(v || 0);
        } catch {
            return `${currency || 'USD'} ${Number(v || 0).toLocaleString(undefined, {
                maximumFractionDigits: maxDecimals,
            })}`;
        }
    };
    return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 shadow-sm">
        <h3 className="mb-4 border-b border-slate-100 dark:border-slate-800 pb-2 text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            {title}
        </h3>
        <div className="mt-4 h-64 w-full">
            {data.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            innerRadius={65}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                            stroke="none"
                        >
                            {data.map((_, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(value: number, name: string) => {
                                const pct =
                                    total > 0
                                        ? ((value / total) * 100).toFixed(2)
                                        : '0.00';
                                return [
                                    `${fmtMoney(value, 2)} (${pct}%)`,
                                    name,
                                ];
                            }}
                            contentStyle={{
                                backgroundColor: '#0f172a',
                                borderColor: '#334155',
                                color: '#f8fafc',
                                borderRadius: '8px',
                            }}
                            itemStyle={{ color: '#fff', fontWeight: 'bold' }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            ) : (
                <div className="flex h-full items-center justify-center text-sm text-slate-400 dark:text-slate-500">
                    No data to chart.
                </div>
            )}
        </div>

        {/* Legend — one row per slice with its colour, name, %-of-portfolio
            and exact $ value, so the allocation is readable at a glance
            without hovering. Scrolls when a breakdown has many buckets. */}
        {data.length > 0 && (
            <ul className="mt-4 max-h-40 space-y-2 overflow-y-auto border-t border-slate-100 pt-4 dark:border-slate-800">
                {data.map((entry, index) => {
                    const pct =
                        total > 0 ? (entry.value / total) * 100 : 0;
                    return (
                        <li
                            key={entry.name}
                            className="flex items-center justify-between gap-2 text-xs"
                        >
                            <div className="flex min-w-0 items-center gap-2">
                                <span
                                    className="h-2.5 w-2.5 flex-shrink-0 rounded-sm"
                                    style={{
                                        backgroundColor:
                                            COLORS[index % COLORS.length],
                                    }}
                                />
                                {/* ADD THIS TITLE ATTRIBUTE so hovering reveals full text */}
                                <span
                                    className="truncate font-medium text-slate-600 dark:text-slate-300"
                                    title={entry.name}
                                >
                                    {entry.name}
                                </span>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-2 font-mono">
                                <span className="font-semibold text-slate-800 dark:text-slate-100">
                                    {pct.toFixed(1)}%
                                </span>
                                <span className="text-slate-400 dark:text-slate-500">
                                    {fmtMoney(entry.value, 0)}
                                </span>
                            </div>
                        </li>
                    );
                })}
            </ul>
        )}
    </div>
    );
};

// --- KPI card ----------------------------------------------------------
const KpiCard = ({
    label,
    value,
    valueClass,
    footnote,
    badge,
}: {
    label: string;
    value: string;
    valueClass: string;
    footnote: string;
    badge?: string;
}) => (
    <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
        {badge && (
            <div className="absolute right-0 top-0 rounded-bl-lg bg-blue-600/20 px-2 py-0.5 text-[9px] font-bold tracking-wider text-blue-600 dark:text-blue-400">
                {badge}
            </div>
        )}
        <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {label}
        </p>
        <h2 className={`font-mono text-2xl font-bold tracking-tight ${valueClass}`}>
            {value}
        </h2>
        <p className="mt-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            {footnote}
        </p>
    </div>
);

export const PortfolioDashboardPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState<DashboardResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [benchmark, setBenchmark] = useState('SPY');
    const [isChartRefreshing, setIsChartRefreshing] = useState(false);
    // Loading guard for the "View In" reporting-currency (lens) flip.
    const [isUpdatingCcy, setIsUpdatingCcy] = useState(false);
    // Period is lifted here so the "Return vs …" KPI card and the performance
    // chart share one selection and always report the same window (Fix 4).
    const [period, setPeriod] = useState<Period>('YTD');

    // Same rebase the chart uses, so the KPI number matches the curve exactly.
    const periodPerf = useMemo(
        () => computeRebasedPerformance(data?.chart_data ?? [], period),
        [data, period],
    );

    useEffect(() => {
        if (!id) return;
        let mounted = true;

        // `showSpinner` on the first load only; Buy/Sell/Edit/Delete actions
        // fire a silent background refresh so the dashboard updates seamlessly
        // without a full-page loading flash or a hard browser refresh.
        const loadData = (showSpinner = false) => {
            if (showSpinner) setIsLoading(true);
            setIsChartRefreshing(true);
            // Pass the selected benchmark so the chart series realigns on change.
            getPortfolioDashboard(Number(id), benchmark)
                .then((res) => {
                    if (mounted) {
                        setData(res);
                        setIsLoading(false);
                        setIsChartRefreshing(false);
                    }
                })
                .catch((err) => {
                    // eslint-disable-next-line no-console
                    console.error(err);
                    if (mounted) {
                        setIsLoading(false);
                        setIsChartRefreshing(false);
                    }
                });
        };

        loadData(true);

        // Re-run the Numba engine whenever a row action dispatches this event.
        const onUpdated = () => loadData(false);
        window.addEventListener('portfolio-updated', onUpdated);
        return () => {
            mounted = false;
            window.removeEventListener('portfolio-updated', onUpdated);
        };
    }, [id, benchmark]);

    // Flip the portfolio's reporting base currency (the "Dynamic Lens"). Sends a
    // partial PATCH (only ``base_currency``) then fires the shared refresh event
    // so the backend re-runs its Any-to-Any FX engine and repaints every KPI,
    // market value and P&L in the newly-selected currency — no page reload.
    const handleBaseCurrencyChange = async (newCcy: CurrencyCode) => {
        if (!id || !data) return;
        setIsUpdatingCcy(true);
        setIsChartRefreshing(true); // Fades the UI to show activity
        try {
            await updatePortfolio(Number(id), { base_currency: newCcy });
            // Trigger instant backend recalculation via the dashboard listener.
            window.dispatchEvent(new Event('portfolio-updated'));
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            alert('Failed to change reporting currency.');
            setIsChartRefreshing(false);
        } finally {
            setIsUpdatingCcy(false);
        }
    };

    const handleExport = async () => {
        if (!id) return;
        setIsExporting(true);
        try {
            await exportPortfolioCsv(Number(id), `portfolio_${id}.csv`);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
            alert('Could not export the ledger. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const renderBody = () => {
        if (isLoading) {
            return (
                <div className="animate-pulse py-16 text-center font-medium text-slate-400">
                    Crunching ledger math &amp; fetching live quotes…
                </div>
            );
        }
        // Bug 2 FIX: Provide a helpful empty state with a return action
        if (!data || !data.metrics) {
            return (
                <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/20 py-24 mt-12 text-center max-w-2xl mx-auto">
                    <h3 className="mb-2 text-xl font-semibold text-slate-900 dark:text-white">
                        Dashboard Not Found
                    </h3>
                    <p className="mb-6 max-w-sm text-slate-500 dark:text-slate-400">
                        This portfolio could not be loaded. It may have been deleted, or there was a server error.
                    </p>
                    <button
                        onClick={() => navigate('/dashboard/my-portfolios')}
                        className="rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
                    >
                        &larr; Back to My Portfolios
                    </button>
                </div>
            );
        }

        const { metrics, positions, performers } = data;

        // The portfolio's reporting base currency — every aggregate/KPI below is
        // expressed in it, so the whole dashboard renders the correct glyph.
        const baseCcy = data.base_currency || metrics.base_currency || 'USD';

        // Human-readable label for the currently-selected benchmark so the
        // "Return vs …" KPI card + the chart both track the dropdown choice.
        const benchmarkLabel =
            AVAILABLE_BENCHMARKS.find((b) => b.symbol === benchmark)?.label ||
            benchmark;

        // Allocation donut — open positions only, largest first. Every slice
        // uses the BASE-currency market value so a mixed-currency book sums
        // apples-to-apples through the reporting-currency lens.
        const openPositions = positions.filter(
            (p) => p.total_market_value_base > 0,
        );
        const totalMarketValue = openPositions.reduce(
            (sum, p) => sum + p.total_market_value_base,
            0,
        );

        // By individual holding (market value).
        const pieData = [...openPositions]
            .sort((a, b) => b.total_market_value_base - a.total_market_value_base)
            .map((p) => ({ name: p.symbol, value: p.total_market_value_base }));

        // Generic bucketing helper: sum market value grouped by a key, largest
        // slice first, with a graceful fallback label for missing metadata.
        const aggregateBy = (
            keyFn: (p: PositionRow) => string,
        ): { name: string; value: number }[] => {
            const buckets = new Map<string, number>();
            for (const p of openPositions) {
                const key = keyFn(p);
                buckets.set(
                    key,
                    (buckets.get(key) ?? 0) + p.total_market_value_base,
                );
            }
            return Array.from(buckets.entries())
                .map(([name, value]) => ({ name, value }))
                .sort((a, b) => b.value - a.value);
        };

        // By sector. Cleans up legacy database string prefixes so old
        // rows don't break the new beautiful sector grouping.
        const sectorData = aggregateBy((p) => {
            let s = p.sector || 'Uncategorized';
            if (s.startsWith('Stock - ')) {
                s = s.replace('Stock - ', '');
            } else if (s === 'Stock (Other)' || s === 'Stock') {
                s = 'Uncategorized';
            }
            return s;
        });

        // By asset class. Converts DB ENUMs beautifully (e.g., "STOCK" -> "Stock")
        const assetClassData = aggregateBy((p) => {
            const typeStr = (p.instrument_type || 'Other').trim().toUpperCase();
            if (typeStr === 'STOCK') return 'Stock';
            if (typeStr === 'ETF') return 'ETF';
            if (typeStr === 'REIT') return 'REIT';
            if (typeStr === 'CRYPTO') return 'Crypto';
            return 'Other';
        });

        return (
            <>
                {/* Header row: back + title + export */}
                <div className="mb-8 flex flex-col gap-4 border-b border-slate-200 dark:border-slate-800 pb-6 md:flex-row md:items-center md:justify-between">
                    <div>
                        <button
                            onClick={() => navigate('/dashboard/my-portfolios')}
                            className="mb-2 flex items-center gap-1 text-sm font-medium text-slate-500 transition hover:text-blue-500"
                        >
                            &larr; Back to Hub
                        </button>
                        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                            {data.name}
                        </h1>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                            Real-time ledger matching and tax-lot analysis.
                        </p>
                    </div>
                    {/* Currency Lens + Benchmark selector + export */}
                    <div className="flex flex-col items-center gap-3 sm:flex-row">
                        {/* THE DYNAMIC LENS TOGGLE — instantly re-bases the whole
                            portfolio's reporting currency via a partial PATCH. */}
                        <div className="flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 shadow-sm transition">
                            <span className="text-[10px] font-bold text-blue-800 dark:text-blue-300 uppercase tracking-wider">
                                View In:
                            </span>
                            <select
                                value={baseCcy}
                                onChange={(e) =>
                                    handleBaseCurrencyChange(
                                        e.target.value as CurrencyCode,
                                    )
                                }
                                disabled={isChartRefreshing || isUpdatingCcy}
                                className="bg-transparent text-sm font-bold text-blue-700 dark:text-blue-400 cursor-pointer focus:outline-none disabled:opacity-50"
                                title="Portfolio Reporting Currency"
                            >
                                <option value="USD">$ USD</option>
                                <option value="EUR">€ EUR</option>
                                <option value="GBP">£ GBP</option>
                            </select>
                        </div>

                        <select
                            value={benchmark}
                            onChange={(e) => setBenchmark(e.target.value)}
                            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm font-medium text-slate-800 dark:text-slate-200 shadow-sm transition focus:border-blue-500 focus:outline-none"
                            disabled={isChartRefreshing}
                        >
                            {AVAILABLE_BENCHMARKS.map((b) => (
                                <option key={b.symbol} value={b.symbol}>
                                    vs {b.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={handleExport}
                            disabled={isExporting}
                            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-800 px-5 py-2.5 text-sm font-medium text-slate-800 dark:text-white shadow-sm transition hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
                        >
                            {isExporting ? 'Exporting…' : '↓ Export Ledger (CSV)'}
                        </button>
                    </div>
                </div>

                {/* KPI strip */}
                <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
                    <KpiCard
                        label="Total Market Value"
                        value={money(metrics.total_market_value, baseCcy)}
                        valueClass="text-slate-900 dark:text-white"
                        footnote={`Live value of open positions (${baseCcy})`}
                    />
                    <KpiCard
                        label="Unrealized Return"
                        value={`${metrics.simple_return_percent > 0 ? '+' : ''}${(metrics.simple_return_percent || 0).toFixed(2)}%`}
                        valueClass={moneyColor(metrics.simple_return_percent)}
                        footnote="Unrealized return vs cost basis for open positions (money-in vs market value)"
                    />
                    <KpiCard
                        label="Total Unrealized P&L"
                        value={signedMoney(metrics.total_unrealized_currency, baseCcy)}
                        valueClass={moneyColor(metrics.total_unrealized_currency)}
                        footnote={`${(metrics.total_unrealized_percent || 0).toFixed(2)}% of Cost Basis`}
                    />
                    <KpiCard
                        label="Total Realized P&L"
                        value={signedMoney(metrics.total_realized_currency, baseCcy)}
                        valueClass={moneyColor(metrics.total_realized_currency)}
                        footnote="From Closed Tax Lots (FIFO)"
                    />
                    <KpiCard
                        label={`Return vs ${benchmarkLabel}`}
                        badge={period}
                        value={`${periodPerf.portPeriodPct > 0 ? '+' : ''}${periodPerf.portPeriodPct.toFixed(2)}%  |  ${periodPerf.benchPeriodPct.toFixed(2)}%`}
                        valueClass={moneyColor(periodPerf.portPeriodPct)}
                        footnote={`Portfolio vs ${benchmarkLabel}`}
                    />
                    <KpiCard
                        label="All-Time ROI (Net)"
                        value={`${metrics.all_time_roi > 0 ? '+' : ''}${(metrics.all_time_roi || 0).toFixed(2)}%`}
                        valueClass={moneyColor(metrics.all_time_roi)}
                        footnote="Total Profit vs Total Capital"
                    />
                </div>

                {/* --- ADVANCED PERFORMANCE CHART --- */}
                <div
                    className={`transition-opacity duration-200 ${isChartRefreshing ? 'opacity-50' : 'opacity-100'}`}
                >
                    <PortfolioPerformanceChart
                        data={data.chart_data || []}
                        benchmarkLabel={benchmarkLabel}
                        period={period}
                        onPeriodChange={setPeriod}
                    />
                </div>

                {/* Allocation row: three side-by-side donuts (market value,
                    sector, asset class), each with exact %-of-portfolio tooltips. */}
                <div className="mb-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
                    <AllocationDonut
                        title="Allocation by Market Value"
                        data={pieData}
                        total={totalMarketValue}
                        currency={baseCcy}
                    />
                    <AllocationDonut
                        title="Allocation by Sector"
                        data={sectorData}
                        total={totalMarketValue}
                        currency={baseCcy}
                    />
                    <AllocationDonut
                        title="Allocation by Asset Class"
                        data={assetClassData}
                        total={totalMarketValue}
                        currency={baseCcy}
                    />
                </div>

                {/* Performer widgets — placed below the allocation charts. */}
                <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <PerformersList
                        title="Top Overall (All-time)"
                        list={performers?.top_overall}
                        metricKey="unrealized_pnl_percent"
                    />
                    <PerformersList
                        title="Best Today"
                        list={performers?.best_daily}
                        metricKey="daily_pnl_percent"
                    />
                    <PerformersList
                        title="Worst Overall (All-time)"
                        list={performers?.worst_overall}
                        metricKey="unrealized_pnl_percent"
                    />
                    <PerformersList
                        title="Worst Today"
                        list={performers?.worst_daily}
                        metricKey="daily_pnl_percent"
                    />
                </div>

                {/* Ledger grid */}
                <div className="mb-4">
                    <div className="mb-4 flex items-end justify-between">
                        <h3 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                            Portfolio Positions and Performance
                        </h3>
                        <p className="hidden text-xs text-slate-400 dark:text-slate-500 sm:block">
                            Click any column header to sort
                        </p>
                    </div>
                    <PortfolioPositionsTable positions={positions} />
                </div>

                {/* Realized History — the closed tax-lots created by SELL events. */}
                <TradeHistoryTable history={data.closed_history} />
            </>
        );
    };

    return (
        <Layout sidebar={<Sidebar />}>
            {/* Platform-wide asset search — always available from any
                portfolio page so the user can jump to any deep-dive. */}
            <div className="mb-8">
                <GlobalSearchBar />
            </div>
            {renderBody()}
        </Layout>
    );
};

export default PortfolioDashboardPage;
